// TeacherApplicationForm.jsx
import React, { useState } from "react";
import { Box, Typography, Button, Alert, AlertTitle, Stack } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { submitTeacherApplication } from "../../../redux/Actions/teacherProfileActions";
import { CONTACT_EMAIL } from "config/contact";

const TeacherApplicationForm = ({ onClose, pageMode = false }) => {
  const dispatch = useDispatch();
  const profile = useSelector((s) => s.teacherProfile.profile);

  const [cvFile, setCvFile] = useState(null);
  const [certFile, setCertFile] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState(""); // "success" | "error"
  const [submitting, setSubmitting] = useState(false);
  const alreadySubmitted = !!profile?.applicationSubmitted;

  const acceptPdf = ".pdf";

  const onPick = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!ok) {
      setStatusMessage("❌ Only PDF files are allowed.");
      setStatusType("error");
      return;
    }
    setter(file);
    setStatusMessage("");
    setStatusType("");
  };

  const handleSubmit = async () => {
    if (!cvFile || !certFile) {
      setStatusMessage("Please upload both your CV and your Certificate (PDF).");
      setStatusType("error");
      return;
    }
    setSubmitting(true);
    const res = await dispatch(submitTeacherApplication(cvFile, certFile));
    setSubmitting(false);

    if (res.success) {
      setStatusMessage("✅ Application submitted. You’ll be notified by email.");
      setStatusType("success");
    } else {
      setStatusMessage(`❌ ${res.message || "Submission failed."}`);
      setStatusType("error");
    }
  };

  const disabled = submitting || alreadySubmitted;

  return (
    <Box sx={{ p: 3, width: pageMode ? "100%" : 420, maxWidth: pageMode ? "100%" : "90vw", mx: "auto", bgcolor: "background.paper", borderRadius: 3, boxShadow: pageMode ? 1 : 0 }}>
      <Typography variant="h5" gutterBottom>
        Teacher Application
      </Typography>


      {statusMessage && (
        <Alert severity={statusType || "info"} sx={{ mb: 2 }}>
          {statusMessage}
        </Alert>
      )}

      {alreadySubmitted && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Application submitted</AlertTitle>
          Thank you! Your application has already been submitted and cannot be submitted again. We will review it and get back to you soon.
          You’ll be informed by email as the process moves forward.
        </Alert>
      )}

         <Alert severity="info" sx={{ mb: 2 }}>
        If you experience issues, email your PDFs to <strong>{CONTACT_EMAIL}</strong>.
        </Alert>

      <Stack spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" component="label" disabled={disabled}>
          Upload CV (PDF)
          <input type="file" hidden accept={acceptPdf} onChange={onPick(setCvFile)} />
        </Button>
        {cvFile && <Typography variant="body2">CV: {cvFile.name}</Typography>}

        <Button variant="contained" component="label" disabled={disabled}>
          Upload Certificate (PDF)
          <input type="file" hidden accept={acceptPdf} onChange={onPick(setCertFile)} />
        </Button>
        {certFile && <Typography variant="body2">Certificate: {certFile.name}</Typography>}
      </Stack>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        {!pageMode && <Button variant="outlined" onClick={onClose}>Cancel</Button>}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={disabled || !cvFile || !certFile}
        >
          {submitting ? "Submitting..." : "Submit Application"}
        </Button>
      </Stack>
    </Box>
  );
};

export default TeacherApplicationForm;

